import { Day } from './day.js'

const REGEX_YYYY_MM = /^(\d{4})[-|/](\d{2})$/
const REGEX_MM_YYYY = /^(\d{2})[-|/](\d{4})$/

export class Month {
    private readonly year: number

    /** 1-based: 1 = January, ..., 12 = December */
    private readonly monthOfYear: number

    public constructor(year: number, monthOfYear: number) {
        if (monthOfYear < 1 || monthOfYear > 12) {
            throw new Error('Invalid month: ' + monthOfYear)
        }

        this.year = year
        this.monthOfYear = monthOfYear
    }

    public static fromYm(ym: string): Month {
        const match = ym.match(REGEX_YYYY_MM)

        if (!match) {
            throw new Error('Invalid month string: ' + ym)
        }

        const year = Number(match[1])
        const monthOfYear = Number(match[2])

        return new Month(year, monthOfYear)
    }

    public static fromMy(my: string): Month {
        const match = my.match(REGEX_MM_YYYY)

        if (!match) {
            throw new Error('Invalid month string: ' + my)
        }

        const year = Number(match[2])
        const monthOfYear = Number(match[1])

        return new Month(year, monthOfYear)
    }

    public toYmString(): string {
        return `${this.year}-${this.monthOfYear.toString().padStart(2, '0')}` // YYYY-MM
    }

    public toMyString(): string {
        return `${this.monthOfYear.toString().padStart(2, '0')}-${this.year}` // MM-YYYY
    }

    public firstDay(): Day {
        return Day.fromNumeric(this.year, this.monthOfYear, 1)
    }

    public lastDay(): Day {
        return this.firstDay().lastOfMonth()
    }

    public includes(day: Day) {
        return day.year() === this.year && day.month() === this.monthOfYear
    }

    public addMonths(count: number): Month {
        // Zero-based month allows simpler division and modulo operations
        const zeroBasedMonthWithOverflow = this.monthOfYear + count - 1
        const year = this.year + Math.floor(zeroBasedMonthWithOverflow / 12)
        let zeroBasedMonth = zeroBasedMonthWithOverflow % 12

        if (zeroBasedMonth < 0) {
            zeroBasedMonth += 12
            // No need to tweak year as Math.floor always rounds down (e.g. -1.3 -> -2, not -1)
        }

        const oneBasedMonth = zeroBasedMonth + 1
        return new Month(year, oneBasedMonth)
    }

    /**
     * Returns the next month.
     */
    public next(): Month {
        return this.addMonths(1)
    }

    /**
     * Compares this month with another month.
     * Returns a negative number if this month is before the other,
     * a positive number if this month is after the other,
     * or zero if they are the same.
     */
    private compare(other: Month): number {
        if (this.year !== other.year) {
            return this.year - other.year
        }
        return this.monthOfYear - other.monthOfYear
    }

    /**
     * Returns true if this month is the same as the other month.
     */
    public isSame(other: Month): boolean {
        return this.compare(other) === 0
    }

    /**
     * Returns true if this month is before the other month.
     */
    public isBefore(other: Month): boolean {
        return this.compare(other) < 0
    }

    /**
     * Returns true if this month is after the other month.
     */
    public isAfter(other: Month): boolean {
        return this.compare(other) > 0
    }

    /**
     * Returns true if this month is the same or before the other month.
     */
    public isSameOrBefore(other: Month): boolean {
        return this.compare(other) <= 0
    }

    /**
     * Returns true if this month is the same or after the other month.
     */
    public isSameOrAfter(other: Month): boolean {
        return this.compare(other) >= 0
    }
}
